import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Instagram, 
  Facebook, 
  Music2, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  MessageSquare, 
  Clock, 
  Target, 
  Filter, 
  ArrowRight, 
  Zap, 
  Sparkles,
  BarChart3,
  X,
  Layers,
  MousePointer2,
  ChevronRight
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip 
} from 'recharts';
import './ContentRevenue.css';

const ContentRevenue = () => {
  const [activePlatform, setActivePlatform] = useState('All');
  const [contentType, setContentType] = useState('All');
  const [selectedItem, setSelectedItem] = useState(null);

  const contentData = [
    {
      id: '1',
      thumbnail: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&q=80&w=400',
      title: 'Winter Glow Collection Launch',
      type: 'Reel',
      platform: 'Instagram',
      revenue: 24200,
      dms: 1240,
      convRate: 8.4,
      avgTimeToPurchase: '4.2h',
      performance: 12,
      aov: 145,
    },
    {
      id: '2',
      thumbnail: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&q=80&w=400',
      title: 'Midnight Velvet Close-up',
      type: 'Post',
      platform: 'Instagram',
      revenue: 18500,
      dms: 842,
      convRate: 6.2,
      avgTimeToPurchase: '12h',
      performance: 5,
      aov: 110,
    },
    {
      id: '3',
      thumbnail: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&q=80&w=400',
      title: 'Styling the Emerald Blazer',
      type: 'Video',
      platform: 'TikTok',
      revenue: 31200,
      dms: 2150,
      convRate: 4.8,
      avgTimeToPurchase: '2.5h',
      performance: 18,
      aov: 98,
    },
    {
      id: '4',
      thumbnail: 'https://images.unsplash.com/photo-1539109132304-64d47d48d08c?auto=format&fit=crop&q=80&w=400',
      title: 'Customer Feedback Showcase',
      type: 'Post',
      platform: 'Facebook',
      revenue: 12400,
      dms: 420,
      convRate: 9.1,
      avgTimeToPurchase: '18h',
      performance: -2,
      aov: 165,
    },
    {
      id: '5',
      thumbnail: 'https://images.unsplash.com/photo-1558769132-cb1aea458c5e?auto=format&fit=crop&q=80&w=400',
      title: 'Morning Routine x Revexia',
      type: 'Reel',
      platform: 'Instagram',
      revenue: 15900,
      dms: 910,
      convRate: 7.3,
      avgTimeToPurchase: '6h',
      performance: 8,
      aov: 132,
    }
  ];

  const filteredContent = useMemo(() => {
    return contentData.filter(item => {
      const platformMatch = activePlatform === 'All' || item.platform === activePlatform;
      const typeMatch = contentType === 'All' || item.type === contentType;
      return platformMatch && typeMatch;
    }).sort((a, b) => b.revenue - a.revenue);
  }, [activePlatform, contentType]);

  const detailFlowSteps = [
    { icon: Target, label: 'Post Reach', val: '124K', drop: null },
    { icon: MessageSquare, label: 'DM Sent', val: '1,240', drop: '99%' },
    { icon: MousePointer2, label: 'Link Click', val: '412', drop: '66%' },
    { icon: DollarSign, label: 'Purchase', val: '104', drop: '74%' },
  ];

  const revenueByTypeData = [
    { name: 'Reels', value: 58200 },
    { name: 'Posts', value: 31400 },
    { name: 'Videos', value: 42100 },
    { name: 'Stories', value: 12500 },
  ];

  const insights = [
    { 
      title: "Reels Revenue Dominance", 
      desc: "Reels drive 48% higher AOV than static posts. Increasing video frequency during peak morning hours could yield an estimated $12K monthly uplift.", 
      icon: Layers, 
      color: "purple",
      metric: "48% Higher AOV"
    },
    { 
      title: "The Golden Hour Effect", 
      desc: "Assets posted between 6PM and 9PM have a 22% faster conversion velocity. DMs triggered in this window close in under 4 hours on average.", 
      icon: Clock, 
      color: "blue",
      metric: "22% Faster Velocity"
    },
    { 
      title: "Direct Intent Triggers", 
      desc: "Close-up product shots trigger 'Buying Inquiry' tags 4x more often. Consider shifting focus from lifestyle to detailed product showcases.", 
      icon: Sparkles, 
      color: "orange",
      metric: "4x Intent Signal"
    }
  ];

  const getPlatformIcon = (platform) => {
    switch(platform) {
      case 'Instagram': return <Instagram size={14} />;
      case 'TikTok': return <Music2 size={14} />;
      case 'Facebook': return <Facebook size={14} />;
      default: return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Content Revenue - Revexia AI Revenue Brain</title>
        <meta name="description" content="Track revenue ROI of social media content" />
      </Helmet>

      <div className="content-revenue-wrapper">
        <div className="container-fluid px-4">
          {/* Header with Filters */}
          <header className="content-revenue-header">
            <p className="header-description">
              Identify the direct revenue ROI of your social media assets
            </p>
            
            <div className="header-filters">
              <div className="platform-filter-group">
                {['All', 'Instagram', 'TikTok', 'Facebook'].map(p => (
                  <button 
                    key={p}
                    onClick={() => setActivePlatform(p)}
                    className={`platform-filter-btn ${activePlatform === p ? 'active' : ''}`}
                  >
                    {p}
                  </button>
                ))}
              </div>

              <div className="content-type-filter-group">
                {['All', 'Reel', 'Post', 'Video'].map(t => (
                  <button 
                    key={t}
                    onClick={() => setContentType(t)}
                    className={`content-type-btn ${contentType === t ? 'active' : ''}`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <button className="filter-icon-btn-content">
                <Filter size={20} />
              </button>
            </div>
          </header>

          {/* Stats Grid */}
          <section className="row g-4 mb-5">
            <div className="col-12 col-sm-6 col-xl-3">
              <div className="stat-card-content glow-orange">
                <div className="stat-card-glow orange"></div>
                <div className="stat-card-content-inner">
                  <div className="stat-card-icon orange">
                    <DollarSign size={20} />
                  </div>
                  <div className="stat-card-info">
                    <p className="stat-card-label">Direct Content Revenue</p>
                    <p className="stat-card-value">$126,840</p>
                    <div className="stat-card-trend positive">
                      <TrendingUp size={14} /> 24.2%
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3">
              <div className="stat-card-content glow-blue">
                <div className="stat-card-glow blue"></div>
                <div className="stat-card-content-inner">
                  <div className="stat-card-icon blue">
                    <BarChart3 size={20} />
                  </div>
                  <div className="stat-card-info">
                    <p className="stat-card-label">Avg. Revenue Per Asset</p>
                    <p className="stat-card-value">$4,102</p>
                    <div className="stat-card-trend positive">
                      <TrendingUp size={14} /> 12.8%
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3">
              <div className="stat-card-content glow-purple">
                <div className="stat-card-glow purple"></div>
                <div className="stat-card-content-inner">
                  <div className="stat-card-icon purple">
                    <Zap size={20} />
                  </div>
                  <div className="stat-card-info">
                    <p className="stat-card-label">Conversion Efficiency</p>
                    <p className="stat-card-value">8.42%</p>
                    <div className="stat-card-trend positive">
                      <TrendingUp size={14} /> 4.5%
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3">
              <div className="stat-card-content glow-cyan">
                <div className="stat-card-glow cyan"></div>
                <div className="stat-card-content-inner">
                  <div className="stat-card-icon cyan">
                    <Clock size={20} />
                  </div>
                  <div className="stat-card-info">
                    <p className="stat-card-label">Avg. Purchase Window</p>
                    <p className="stat-card-value">6.2 Hours</p>
                    <div className="stat-card-trend negative">
                      <TrendingDown size={14} /> 18.4%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Charts Row */}
          <div className="row g-4 mb-5">
            <div className="col-12 col-lg-5">
              <div className="chart-card-revenue">
                <div className="chart-card-header">
                  <div>
                    <h4 className="chart-card-title">Revenue Distribution</h4>
                    <p className="chart-card-subtitle">Comparison of performance by content format</p>
                  </div>
                </div>
                <div className="chart-card-body">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueByTypeData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(13, 15, 41, 0.95)', 
                          border: '1px solid rgba(255,255,255,0.1)', 
                          borderRadius: '12px'
                        }}
                        itemStyle={{ color: '#fff' }}
                      />
                      <Bar dataKey="value" fill="#F97316" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-7">
              <div className="heatmap-placeholder">
                <BarChart3 size={48} className="heatmap-icon" />
                <p>Heatmap Visualization</p>
              </div>
            </div>
          </div>

          {/* Top Earning Assets Table */}
          <section className="top-earning-section">
            <div className="top-earning-glow"></div>
            
            <div className="top-earning-header">
              <div className="top-earning-header-left">
                <div className="pulse-indicator"></div>
                <h3 className="top-earning-title">Top Earning Assets</h3>
              </div>
              <span className="top-earning-badge">Top 50 Real-time</span>
            </div>

            <div className="table-responsive">
              <table className="content-table">
                <thead>
                  <tr>
                    <th>Visual Asset</th>
                    <th>Attributed Revenue</th>
                    <th>Conversations</th>
                    <th className="text-end">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContent.map((item) => (
                    <tr 
                      key={item.id} 
                      onClick={() => setSelectedItem(item)}
                      className="content-table-row"
                    >
                      <td>
                        <div className="asset-cell">
                          <div className="asset-thumbnail">
                            <img src={item.thumbnail} alt={item.title} />
                            <div className="asset-thumbnail-overlay"></div>
                            <div className="asset-thumbnail-badge">
                              {getPlatformIcon(item.platform)}
                              <span>{item.type}</span>
                            </div>
                          </div>
                          <div className="asset-info">
                            <p className="asset-title">{item.title}</p>
                            <p className="asset-platform">{item.platform}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="revenue-value">${item.revenue.toLocaleString()}</span>
                      </td>
                      <td>
                        <div className="conversations-cell">
                          <div className="conversations-icon">
                            <MessageSquare size={16} />
                          </div>
                          <span className="conversations-count">{item.dms} Threads</span>
                        </div>
                      </td>
                      <td className="text-end">
                        <div className={`trend-badge ${item.performance >= 0 ? 'positive' : 'negative'}`}>
                          {item.performance >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                          {Math.abs(item.performance)}%
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* AI Intelligence Layer */}
          <section className="ai-intelligence-section">
            <div className="ai-intelligence-header">
              <div>
                <h3 className="ai-intelligence-title">AI Intelligence Layer</h3>
                <p className="ai-intelligence-subtitle">Strategic optimizations identified by Revexia neural attribution engine</p>
              </div>
            </div>

            <div className="row g-4">
              {insights.map((insight, i) => (
                <div key={i} className="col-12 col-md-6 col-xl-4">
                  <div className={`insight-card-revenue insight-${insight.color}`}>
                    <div className={`insight-card-glow ${insight.color}`}></div>
                    
                    <div className="insight-card-top">
                      <div className={`insight-card-icon ${insight.color}`}>
                        <insight.icon size={22} />
                      </div>
                      <span className={`insight-card-badge ${insight.color}`}>Actionable</span>
                    </div>

                    <h4 className="insight-card-title">{insight.title}</h4>
                    <p className="insight-card-desc">{insight.desc}</p>

                    <div className="insight-card-footer">
                      <span className="insight-metric">{insight.metric}</span>
                      <button className={`insight-action-btn ${insight.color}`}>
                        Apply Optimization <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Detail Sidebar */}
        {selectedItem && (
          <>
            <div 
              className="detail-sidebar-overlay"
              onClick={() => setSelectedItem(null)}
            ></div>
            <div className="detail-sidebar">
              <div className="detail-sidebar-header">
                <div className="detail-sidebar-header-content">
                  <div className="detail-sidebar-thumbnail">
                    <img src={selectedItem.thumbnail} alt={selectedItem.title} />
                  </div>
                  <div className="detail-sidebar-info">
                    <div className="detail-sidebar-badges">
                      <span className="detail-badge-primary">Revenue Focus</span>
                      <span className="detail-badge-secondary">{selectedItem.platform}</span>
                    </div>
                    <h3 className="detail-sidebar-title">{selectedItem.title}</h3>
                    <p className="detail-sidebar-meta">
                      <Zap size={14} className="text-blue-400" /> High Performance {selectedItem.type}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedItem(null)}
                  className="detail-sidebar-close"
                >
                  <X size={28} />
                </button>
              </div>

              <div className="detail-sidebar-body">
                <div className="conversion-path-section">
                  <div className="conversion-path-header">
                    <h4 className="conversion-path-title">Diagnostic Conversion Path</h4>
                    <span className="conversion-path-badge">Healthy Flow</span>
                  </div>
                  <div className="flow-map-detail">
                    {detailFlowSteps.map((step, index) => (
                      <React.Fragment key={index}>
                        <div className="flow-step-detail">
                          <div className="flow-step-icon-detail">
                            <step.icon size={18} />
                          </div>
                          <div className="flow-step-info-detail">
                            <p className="flow-step-label-detail">{step.label}</p>
                            <p className="flow-step-value-detail">{step.val}</p>
                          </div>
                          {step.drop && (
                            <div className="flow-step-drop-detail">-{step.drop}</div>
                          )}
                        </div>
                        {index < detailFlowSteps.length - 1 && (
                          <ChevronRight size={18} className="flow-connector-detail" />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div className="row g-4">
                  <div className="col-6">
                    <div className="metric-card-detail orange">
                      <div>
                        <p className="metric-card-label">Asset Lifetime Value</p>
                        <p className="metric-card-value">${selectedItem.revenue.toLocaleString()}</p>
                      </div>
                      <div className="metric-card-footnote">
                        <TrendingUp size={16} className="text-emerald-400" />
                        14% above account average
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="metric-card-detail blue">
                      <div>
                        <p className="metric-card-label">Customer Sentiment</p>
                        <p className="metric-card-value">High</p>
                      </div>
                      <div className="metric-card-footnote">
                        <Sparkles size={16} className="text-purple-400" />
                        High intent signals rising
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="detail-sidebar-footer">
                <button className="scale-similar-btn">
                  <Zap size={18} /> Scale Similar
                </button>
              </div>
            </div>
          </>
        )}

        {/* Bottom Action Panel */}
        <div className="bottom-action-panel">
          <div className="bottom-action-content">
            <div className="bottom-action-left">
              <div className="bottom-action-icon">
                <BarChart3 size={24} />
              </div>
              <div>
                <p className="bottom-action-label">Global Opportunity</p>
                <p className="bottom-action-text">Video content yields +42% higher AOV today.</p>
              </div>
            </div>

            <div className="bottom-action-right">
              <button className="launch-campaign-btn">
                Launch Campaign <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContentRevenue;
